<template>
  <div class="input-container">
    <div class="school-input-wrapper" ref="inputWrapperRef">
      <p v-if="label" class="search-placeholder">{{ label }}</p>
      <div class="input-with-icon">
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="search-icon"
          aria-hidden="true"
        >
          <path
            d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z"
            stroke="#6c737f"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M17.5 17.5L13.875 13.875"
            stroke="#6c737f"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <input
          v-model="searchQuery"
          type="text"
          class="school-input"
          :placeholder="placeholder"
          role="combobox"
          :aria-expanded="showDropdown"
          aria-haspopup="listbox"
          @focus="handleInputFocus"
          @input="handleInputChange"
          @blur="handleInputBlur"
        />
      </div>
      <div v-if="showDropdown" class="dropdown-panel" ref="dropdownRef" role="listbox">
        <div v-if="isSearching" class="loading-state" role="status" aria-live="polite">
          <p class="loading-text">Searching schools...</p>
        </div>
        <div v-else-if="filteredSchools.length > 0" class="school-list">
          <div
            v-for="school in filteredSchools"
            :key="school.id"
            class="school-option"
            role="option"
            :aria-selected="isSelected(school)"
            @mousedown.prevent="selectSchool(school)"
          >
            <div class="school-info">
              <p class="school-name">{{ school.name }}</p>
              <p class="school-location">{{ school.location }}</p>
            </div>
            <div v-if="isSelected(school)" class="check-icon">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M16.6667 5L7.50004 14.1667L3.33337 10"
                  stroke="#00925e"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </div>
          <div class="powered-by-separator"></div>
          <div class="powered-by-container">
            <img
              src="/images/powered-by-google.png"
              alt="Powered by Google"
              class="powered-by-logo"
            />
          </div>
        </div>
        <div
          v-else-if="searchQuery && !isSearching"
          class="add-school-option"
          role="option"
          @mousedown.prevent="handleAddSchool"
        >
          <div class="add-school-content">
            <p class="add-school-text">Add this school</p>
            <p class="add-school-hint">{{ searchQuery }}</p>
          </div>
        </div>
      </div>
    </div>
    <!-- School Selected Confirmation -->
    <div v-if="selectedSchool?.name" class="school-selected-confirmation">
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="checkmark-icon"
        aria-hidden="true"
      >
        <path
          d="M16.6667 5L7.50004 14.1667L3.33337 10"
          stroke="#00925e"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <p class="confirmation-text">
        School selected: {{ selectedSchool.name }}
      </p>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import {
  ref,
  computed,
  defineEmits,
  defineProps,
  watch,
  onMounted,
  onUnmounted,
} from "vue";
import userWalkthroughService from "@/services/userWalkthroughService.js"; // Ensure accessible or passed in

const props = defineProps({
  modelValue: {
    type: [Object, Array],
    default: null,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: "School Name",
  },
  placeholder: {
    type: String,
    default: "Type to search...",
  },
  searchFunction: {
    type: Function,
    default: null, // If null, uses default fetchSchools logic
  },
});

const emit = defineEmits(["update:modelValue"]);

const searchQuery = ref(props.multiple ? "" : props.modelValue?.name || "");
const selectedSchool = ref(props.modelValue);
const showDropdown = ref(false);
const isSearching = ref(false);
const inputWrapperRef = ref(null);
const dropdownRef = ref(null);
const schools = ref([]);
const searchTimeout = ref(null);

// Watch for prop changes (external updates)
watch(
  () => props.modelValue,
  (newValue) => {
    // Handling for single select
    if (!props.multiple) {
      const currentId = selectedSchool.value?.id;
      const newId = newValue?.id;
      const currentName = selectedSchool.value?.name;
      const newName = newValue?.name;

      if (currentId !== newId || currentName !== newName) {
        if (newValue) {
          selectedSchool.value = newValue;
          if (!isSearching.value) {
            searchQuery.value = newValue.name || "";
          }
        } else {
          selectedSchool.value = null;
          searchQuery.value = "";
        }
      }
    } else {
      // Handling for multiple select - just sync the local ref if needed
      // Logic for multiple select typically relies on the array passed in
      selectedSchool.value = newValue;
    }
  },
  { immediate: true }
);

const isSelected = (school) => {
  if (!props.modelValue) return false;
  if (props.multiple) {
    if (Array.isArray(props.modelValue)) {
      return props.modelValue.some(
        (s) =>
          (s.id && s.id === school.id) || (s.name && s.name === school.name)
      );
    }
    return false;
  }
  return (
    (props.modelValue.id && props.modelValue.id === school.id) ||
    props.modelValue.name === school.name
  );
};

const filteredSchools = computed(() => {
  return schools.value;
});

const defaultFetchSchools = async (query) => {
  if (!query || query.trim() === "") {
    schools.value = [];
    isSearching.value = false;
    return;
  }

  isSearching.value = true;
  try {
    const response = await userWalkthroughService.getSchools(query.trim());
    if (response?.data?.success && Array.isArray(response.data.data)) {
      schools.value = response.data.data.map((school, index) => ({
        id: index + 1, // Or proper ID if available in this service response
        name: school.title,
        location: school.country,
        originalData: school, // Store original if needed
      }));
    } else {
      schools.value = [];
    }
  } catch (error) {
    console.error("Error fetching schools:", error);
    schools.value = [];
  } finally {
    isSearching.value = false;
  }
};

const fetchSchools = async (query) => {
  if (props.searchFunction) {
    isSearching.value = true;
    try {
      const results = await props.searchFunction(query);
      schools.value = results;
    } catch (e) {
      console.error(e);
      schools.value = [];
    } finally {
      isSearching.value = false;
    }
  } else {
    await defaultFetchSchools(query);
  }
};

const showAddSchoolOption = computed(() => {
  return (
    searchQuery.value &&
    searchQuery.value.trim() !== "" &&
    filteredSchools.value.length === 0 &&
    !isSearching.value
  );
});

const handleInputFocus = () => {
  if (searchQuery.value && searchQuery.value.trim() !== "") {
    if (filteredSchools.value.length === 0 && !isSearching.value) {
      fetchSchools(searchQuery.value);
    }
    showDropdown.value = true;
  } else {
    // Fetch initial suggestions if query is empty (e.g. recently used or default list)
    if (!isSearching.value && schools.value.length === 0) {
      fetchSchools("");
    }
    showDropdown.value = true;
  }
};

const handleInputChange = () => {
  if (searchQuery.value.trim() === "") {
    if (!props.multiple) {
      selectedSchool.value = null;
      emit("update:modelValue", null);
    }
    schools.value = [];
    showDropdown.value = false;
    return;
  }

  if (
    !props.multiple &&
    selectedSchool.value &&
    searchQuery.value !== selectedSchool.value.name
  ) {
    selectedSchool.value = null;
    emit("update:modelValue", null);
  }

  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value);
  }

  searchTimeout.value = setTimeout(() => {
    fetchSchools(searchQuery.value);
  }, 300);

  // Show dropdown immediately if we have results or are typing (to trigger search indicator)
  showDropdown.value = true;
};

const handleInputBlur = (event) => {
  setTimeout(() => {
    if (
      dropdownRef.value &&
      !dropdownRef.value.contains(document.activeElement)
    ) {
      showDropdown.value = false;
    }
  }, 200);
};

const selectSchool = (school) => {
  if (props.multiple) {
    const currentSelection = Array.isArray(props.modelValue)
      ? [...props.modelValue]
      : [];
    const index = currentSelection.findIndex(
      (s) => (s.id && s.id === school.id) || (s.name && s.name === school.name)
    );

    if (index >= 0) {
      currentSelection.splice(index, 1);
    } else {
      currentSelection.push(school);
    }

    emit("update:modelValue", currentSelection);
    // Keep dropdown open for multiple select
    // Clear search query to allow searching for next item
    searchQuery.value = "";
    // Re-fetch default list if needed, or keep current results?
    // User often wants to search for something else, so maybe keeping results is fine
    // but clearing query suggests we might want to reset.
    // Let's keep the results for now but clear the text.
  } else {
    selectedSchool.value = school;
    searchQuery.value = school.name;
    showDropdown.value = false;
    emit("update:modelValue", school);
  }
};

const handleAddSchool = () => {
  if (searchQuery.value && searchQuery.value.trim() !== "") {
    const newSchool = {
      id: null,
      name: searchQuery.value.trim(),
      location: "",
      isNew: true,
    };
    selectedSchool.value = newSchool;
    showDropdown.value = false;
    emit("update:modelValue", newSchool);
  }
};

const handleClickOutside = (event) => {
  if (
    inputWrapperRef.value &&
    !inputWrapperRef.value.contains(event.target) &&
    dropdownRef.value &&
    !dropdownRef.value.contains(event.target)
  ) {
    showDropdown.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value);
  }
});
</script>

<style scoped>
/* Input Container */
.input-container {
  width: 100%;
  position: relative;
}

.school-input-wrapper {
  position: relative;
  width: 100%;
}

.search-placeholder {
  /* Matching text style if needed, or adjust based on desired look */
  margin-bottom: 8px; /* Assuming some spacing */
  color: #3b4648;
  font-size: var(--fs-large);
  font-weight: 400;
  /* If mimicking the AccountContent label style */
}

.input-with-icon {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: 16px;
  z-index: 1;
  pointer-events: none;
}

.school-input {
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 1px solid #d8dae5;
  border-radius: 8px;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #3b4648;
  box-sizing: border-box;
  transition: border-color 0.3s ease;
}

.school-input:focus {
  outline: none;
  border-color: var(--primary-blue);
  background-color: #eaecff;
}

.school-input::placeholder {
  color: #6c737f;
}

/* School Selected Confirmation */
.school-selected-confirmation {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 12px 16px;
  background-color: #e6f4ef;
  border: 1px solid #e6f4ef;
  border-radius: 8px;
  box-sizing: border-box;
}

.checkmark-icon {
  flex-shrink: 0;
}

.confirmation-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: var(--green-dark-1);
  margin: 0;
}

/* Dropdown Panel */
.dropdown-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background-color: white;
  border: 1px solid #d8dae5;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  margin-top: 4px;
}

.school-list {
  display: flex;
  flex-direction: column;
}

.school-option {
  padding: 16px;
  border-bottom: 1px solid #f2f3fb;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.school-option:last-child {
  border-bottom: none;
}

.school-option:hover {
  background-color: #f2f3fb;
}

.school-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.school-name {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #3b4648;
  margin: 0;
}

.school-location {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-small);
  line-height: 20px;
  color: #6c737f;
  margin: 0;
}

/* Add School Option */
.add-school-option {
  padding: 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-top: 1px solid #f2f3fb;
}

.add-school-option:hover {
  background-color: #f2f3fb;
}

.add-school-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.add-school-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: var(--primary-blue);
  margin: 0;
}

.add-school-hint {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-small);
  line-height: 20px;
  color: #6c737f;
  margin: 0;
}

.loading-state {
  padding: 16px;
  text-align: center;
}

.loading-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-small);
  line-height: 20px;
  color: #6c737f;
  margin: 0;
}

.powered-by-separator {
  height: 1px;
  background-color: #f2f3fb;
  margin: 0 16px;
}

.powered-by-container {
  display: flex;
  justify-content: flex-end;
  padding: 12px 16px;
}

.powered-by-logo {
  height: 16px;
  width: auto;
}
</style>
